import ScLink from 'components/Generic/SitecoreWrappers/ScLink';
import { NextImage } from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import useHelpers from 'hooks/helpers';
import React, { useMemo } from 'react';
import styles from './ArticleCard.module.scss';
import Readtime from 'components/includes/Readtime';
import { nameToSlug } from 'model/Article';

type ArticleFields = {
  articleCategory: {
    targetItem: {
      name_1e4c2734dc754d528af8084e6c400e1f: {
        value: string;
      };
    };
  };
  articleImage: string;
  articlePublicationDate: {
    formattedDate: string;
  };
  articleStrapline: {
    value: string;
  };
  articleTitle: {
    value: string;
  };
  articleType: string;
  baseUrl: string;
  id: string;
  category: string;
  name: string;
  publicationDate: string;
  strapline: string;
  template: {
    id: string;
  };
  url?: string;
  articleUrl?: string;
  duration?: {
    value: string;
  };
};

type Props = {
  fields: ArticleFields;
  useLineClamps?: boolean;
  cardCategoryField?: boolean | string;
};

const ArticleCard = ({ fields, cardCategoryField, useLineClamps = true }: Props) => {
  const { t } = useI18n();
  const { localizeDate } = useHelpers();
  const baseUrl = fields.baseUrl;

  const getCategoryText = useMemo(() => {
    if (cardCategoryField) return fields[cardCategoryField];
    return fields.category;
  }, [cardCategoryField, fields]);

  const url = useMemo(() => {
    if (!fields?.url) {
      return baseUrl ? `${baseUrl}/${nameToSlug(fields?.name)}` : '/';
    }
    return fields.url;
  }, [fields, baseUrl]);

  const isTechzoneArticle: boolean = !!fields.articleUrl;

  const scLinkField = useMemo(() => {
    let value;

    if (isTechzoneArticle) {
      value = {
        text: t('readOnTechzone'),
        target: '_blank',
        href: fields.articleUrl,
      };
    } else {
      value = {
        href: url,
      };
    }

    return { value };
  }, [isTechzoneArticle, t, url, fields.articleUrl]);

  return (
    <ScLink
      field={scLinkField}
      showExternalIcon={false}
      data-e2e-component-uid="renderingId"
      className={`${styles['article-card']} hover-text-decoration-none`}
    >
      <div className="d-flex flex-column h-100">
        <div className="flex-shrink-0 position-relative">
          <NextImage
            field={{ src: fields.articleImage }}
            alt={fields.articleTitle.value}
            className={styles['article-card__thumbnail']}
            width={270}
            height={140}
          />
          {isTechzoneArticle && (
            <div className={`${styles['article-card__badge']} text-link vectorBasedBackground`}>
              {t('techzone')}
            </div>
          )}
        </div>
        <div
          className={`${styles['article-card__body']} py-3 px-2 flex-grow-1 vectorBasedBackground`}
        >
          <div className="d-flex align-items-start section-header-small">
            <span className="article-category section-header-small">{getCategoryText}</span>
            <time className="ms-auto section-header-small text-nowrap">
              {localizeDate(fields.publicationDate)}
            </time>
          </div>
          <div className={`mt-2 ${useLineClamps ? 'mb-8' : 'mb-2'}`}>
            <h5
              className={`${styles['article-card__title']} ${useLineClamps ? 'line-clamp-4' : ''}`}
            >
              {fields.articleTitle.value}
            </h5>
            <p className={`body-small text-grey mt-1 ${useLineClamps ? 'line-clamp-3' : ''}`}>
              {fields.articleStrapline.value}
            </p>
          </div>
          <div className="mt-auto">
            {isTechzoneArticle ? (
              <span className="text-link">
                <span>{scLinkField.value.text}</span>
                <i className="bi bi-box-arrow-up-right" />
              </span>
            ) : (
              <Readtime
                articleType={fields.articleType}
                duration={Number(fields.duration?.value ?? '')}
                className="section-header-small"
              />
            )}
          </div>
        </div>
      </div>
    </ScLink>
  );
};

export default ArticleCard;
